import React from "react";
import { Link } from "react-router-dom";

const EndOfTenancy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-cyan-400 via-cyan-500 to-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, white 35px, white 37px)`,
            }}
          ></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm border border-white/40 rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-bold text-white tracking-wide">
                DEPOSIT PROTECTION GUARANTEE
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-none mb-6">
              End Of Tenancy Cleaning Services
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Get your full deposit back with our professional move-out cleaning
              service. 100% satisfaction guaranteed.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/get-quote"
                className="px-8 py-4 bg-white text-navy-900 font-bold rounded-xl hover:bg-gray-100 shadow-lg"
              >
                GET FREE QUOTE
              </Link>
              <a
                href="tel:03300545944"
                className="px-8 py-4 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 border-2 border-white shadow-lg"
              >
                CALL NOW
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Moving out of a rental property is stressful enough without
              worrying about your deposit. Our professional end of tenancy
              cleaning service takes the pressure off, delivering spotless
              results that satisfy even the strictest landlords and letting
              agents. With our comprehensive move out cleaning checklist and
              deposit back guarantee, we've helped thousands of tenants secure
              their full deposit returns.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our trained cleaning specialists know exactly what property
              inspectors look for, ensuring every corner of your rental property
              meets the highest cleanliness standards. Whether you're a tenant
              moving to a new home or a landlord preparing a property for new
              occupants, Shiny Crew Cleaning delivers the thorough, reliable
              service you need.
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              Why Professional Cleaning{" "}
              <span className="text-cyan-500">Matters</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Protect your deposit and save time with our expert service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Meeting Inventory Standards",
                description:
                  "Your tenancy agreement likely includes a clause requiring the property to be returned in the same condition as the check-in inventory. Our cleaning specialists work systematically through every room, matching the cleanliness level expected by property managers.",
                icon: "✅",
                color: "from-cyan-400 to-cyan-600",
              },
              {
                title: "Avoiding Deposit Deductions",
                description:
                  "The average tenant loses 20-40% of their deposit due to cleaning issues. Our rental property cleaning service eliminates this risk by addressing every inspection point, from limescale on taps to grease buildup in kitchen appliances.",
                icon: "💰",
                color: "from-navy-700 to-navy-900",
              },
              {
                title: "Saving Time During Your Move",
                description:
                  "Moving day involves coordinating removals, utility transfers, and address changes. The last thing you need is spending hours scrubbing bathrooms and kitchens. Our professional team handles the entire cleaning process while you focus on settling into your new home.",
                icon: "⏰",
                color: "from-cyan-500 to-navy-700",
              },
              {
                title: "Professional-Grade Results",
                description:
                  "We use commercial cleaning equipment and professional-grade products that deliver results impossible to achieve with household cleaners. Our steam cleaners, carpet shampooers, and specialised degreasers ensure every surface sparkles.",
                icon: "⭐",
                color: "from-coral-400 to-coral-500",
              },
            ].map((benefit, index) => (
              <div key={index} className="relative group">
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${benefit.color} rounded-2xl opacity-5 group-hover:opacity-10`}
                ></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-cyan-400 shadow-lg">
                  <div className="text-5xl mb-4">{benefit.icon}</div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              Comprehensive{" "}
              <span className="text-cyan-500">Cleaning Service</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every area covered with meticulous attention to detail
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Kitchen Deep Cleaning",
                items: [
                  "Oven and hob complete interior cleaning",
                  "Refrigerator/freezer defrost and sanitization",
                  "Cabinets and drawers inside and out",
                  "Countertops and backsplash degreasing",
                  "Sink and taps limescale removal",
                  "Dishwasher and microwave cleaning",
                  "Tiles and grout deep cleaning",
                  "Flooring mopping and degreasing",
                ],
              },
              {
                title: "Bathroom Sanitization",
                items: [
                  "Toilet bowl descaling and sanitization",
                  "Shower/bathtub limescale removal",
                  "Sink and vanity cabinet cleaning",
                  "Tiles and grout mold removal",
                  "Chrome fixtures polishing",
                  "Ventilation fan dust removal",
                  "Walls and ceiling spot cleaning",
                  "Floor disinfection and detailing",
                ],
              },
              {
                title: "Living Areas & Bedrooms",
                items: [
                  "Carpet professional vacuuming",
                  "Hard floors sweeping and mopping",
                  "Windows interior glass cleaning",
                  "Skirting boards dust removal",
                  "Light fixtures dusting",
                  "Radiators deep cleaning",
                  "Door frames and handles",
                  "Built-in wardrobes interior cleaning",
                ],
              },
            ].map((area, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200"
              >
                <h3 className="text-2xl font-bold text-navy-900 mb-6">
                  {area.title}
                </h3>
                <ul className="space-y-3">
                  {area.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deposit Back Guarantee */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div>
              <div className="text-6xl mb-6">🛡️</div>
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                Our{" "}
                <span className="text-cyan-400">Deposit Back Guarantee</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                We stand behind our work with an industry-leading deposit back
                guarantee. If your landlord or letting agent identifies cleaning
                issues that result in deposit deductions, we'll return to
                re-clean those areas at no additional charge.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
                {[
                  {
                    title: "48-Hour Re-Clean Promise",
                    description:
                      "Contact us within 48 hours of inspection, and we'll send our team back immediately.",
                  },
                  {
                    title: "Zero Additional Cost",
                    description:
                      "The re-clean is completely free. We're confident in getting it right the first time.",
                  },
                  {
                    title: "98% First-Time Approval",
                    description:
                      "The vast majority of our clients never need to use the guarantee.",
                  },
                  {
                    title: "Complete Peace of Mind",
                    description:
                      "Focus on your move knowing your deposit is protected by our guarantee.",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-navy-800/50 backdrop-blur-sm border border-cyan-400/30 rounded-xl p-6 text-left"
                  >
                    <h3 className="text-xl font-bold text-cyan-400 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              Transparent <span className="text-cyan-500">Pricing</span>
            </h2>
            <p className="text-xl text-gray-600">
              No hidden fees - what we quote is what you pay
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { size: "Studio", duration: "3-4 hours", team: "2 cleaners" },
              { size: "1-Bedroom", duration: "4-5 hours", team: "2 cleaners" },
              {
                size: "2-Bedroom",
                duration: "5-6 hours",
                team: "2-3 cleaners",
              },
              { size: "3-Bedroom", duration: "6-8 hours", team: "3 cleaners" },
              { size: "4-Bedroom", duration: "8+ hours", team: "3-4 cleaners" },
              {
                size: "Custom Quote",
                duration: "Varies",
                team: "Based on needs",
              },
            ].map((plan, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-cyan-400 shadow-lg"
              >
                <h3 className="text-2xl font-bold text-navy-900 mb-4">
                  {plan.size}
                </h3>
                <div className="space-y-2 mb-6">
                  <p className="text-gray-600">⏱️ {plan.duration}</p>
                  <p className="text-gray-600">👥 {plan.team}</p>
                </div>
                <Link
                  to="/get-quote"
                  className="block w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg text-center"
                >
                  Get Quote
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-600">
              All prices include professional equipment, supplies, and our
              deposit back guarantee
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-cyan-600">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to Secure Your Deposit?
            </h2>
            <p className="text-xl text-navy-900 font-semibold mb-8 max-w-2xl mx-auto">
              Don't risk losing hundreds on deposit deductions. Book your
              professional end of tenancy cleaning today.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/get-quote"
                className="px-10 py-5 bg-navy-900 text-white font-black text-lg rounded-xl hover:bg-navy-800 shadow-2xl"
              >
                GET FREE QUOTE NOW
              </Link>
              <a
                href="tel:03300545944"
                className="px-10 py-5 bg-white text-navy-900 font-black text-lg rounded-xl hover:bg-gray-100 shadow-2xl"
              >
                CALL: 0330 054 5944
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EndOfTenancy;
